import { useLocation } from '@hulu/react-router-dom';
import { useEffect, useRef } from 'react';

import { DCM_AMPLITUDE_ENABLED_FLAG } from '../../../constants/launchDarklyFlags';
import useLaunchDarklyFlag from '../../featureFlags/useLaunchDarkly';
import { RAGE_CLICK_THRESHOLD, RAGE_CLICK_WINDOW_MS } from '../constants';
import { trackRageClick } from '../events';
import { beginPageSession, endPageSession, setAmplitudeTrackingEnabled } from './analyticsClient';
import useAnalyticsUserIdentification from './useAnalyticsUserIdentification';

type Props = {
  children: React.ReactNode;
};

const getElementSelector = (target: EventTarget | null): string | null => {
  if (!(target instanceof Element)) {
    return null;
  }

  const testId = target.closest('[data-testid]')?.getAttribute('data-testid');
  if (testId) {
    return `[data-testid="${testId}"]`;
  }

  const tagName = target.tagName.toLowerCase();
  const role = target.getAttribute('role');
  const ariaLabel = target.getAttribute('aria-label');

  if (ariaLabel) {
    return `${tagName}[aria-label]`;
  }

  if (role) {
    return `${tagName}[role="${role}"]`;
  }

  return tagName;
};

const AmplitudeProvider = ({ children }: Props): JSX.Element => {
  const { pathname } = useLocation();
  const previousPathRef = useRef<string | null>(null);
  const { value: enabled, loading } = useLaunchDarklyFlag(DCM_AMPLITUDE_ENABLED_FLAG, false);
  const isTrackingActive = enabled && !loading;

  useAnalyticsUserIdentification(isTrackingActive);

  useEffect(() => {
    if (loading) {
      return;
    }

    setAmplitudeTrackingEnabled(enabled);
  }, [enabled, loading]);

  useEffect(() => {
    if (!isTrackingActive) {
      return;
    }

    const clickHistory = new Map<string, number[]>();

    const handleClick = (event: MouseEvent): void => {
      const selector = getElementSelector(event.target);
      if (!selector) {
        return;
      }

      const now = Date.now();
      const timestamps = (clickHistory.get(selector) ?? []).filter(
        (timestamp) => now - timestamp <= RAGE_CLICK_WINDOW_MS
      );

      timestamps.push(now);
      clickHistory.set(selector, timestamps);

      if (timestamps.length >= RAGE_CLICK_THRESHOLD) {
        trackRageClick(selector, timestamps.length);
        clickHistory.delete(selector);
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => document.removeEventListener('click', handleClick, true);
  }, [isTrackingActive]);

  useEffect(() => {
    if (!isTrackingActive) {
      if (previousPathRef.current) {
        endPageSession();
        previousPathRef.current = null;
      }
      return;
    }

    if (previousPathRef.current === pathname) {
      return;
    }

    if (previousPathRef.current) {
      endPageSession();
    }

    beginPageSession(pathname);
    previousPathRef.current = pathname;
  }, [isTrackingActive, pathname]);

  useEffect(() => {
    if (!isTrackingActive) {
      return;
    }

    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') {
        endPageSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isTrackingActive]);

  return <>{children}</>;
};

export default AmplitudeProvider;
